import { Component, inject, input, OnInit, signal } from '@angular/core';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ColDef, GridOptions } from 'ag-grid-community';
import { SubscriptionService } from 'src/app/service/subscription.service';
import { defaultTableDefOptions } from '../../helpers';
import { NotifyService } from '../../service/notify.service';
import { BranchImpl, Bundle, PurchaseBundleEntry } from '../../types';
import { StripePurchaseBundleUiActionsComponent } from './stripe-purchase-bundle-ui-actions/stripe-purchase-bundle-ui-actions.component';
import { StripePurchaseBundleUiCartActionsComponent } from './stripe-purchase-bundle-ui-cart-actions/stripe-purchase-bundle-ui-cart-actions.component';

@Component({
  selector: 'app-stripe-purchase-bundle-ui',
  templateUrl: './stripe-purchase-bundle-ui.component.html',
  styleUrls: ['./stripe-purchase-bundle-ui.component.scss'],
  standalone: false,
})
export class StripePurchaseBundleUiComponent implements OnInit {
  private subscriptionService = inject(SubscriptionService);
  private notifyService = inject(NotifyService);

  public saving = signal<boolean>(false);
  readonly branch = input<Partial<BranchImpl>>(undefined);

  bundles = signal<Bundle[]>([]);
  cart = signal<PurchaseBundleEntry[]>([]);
  isLoading = signal<boolean>(false);

  transactionDetails = new FormGroup({
    internal_po: new FormControl<string>('', [Validators.required]),
    customer_po: new FormControl(''),
    ship_by: new FormControl(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
    shipping_notes: new FormControl<string>(''),
  });

  public readonly bundleTableDef: GridOptions<Bundle> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly bundleColumnDefs: ColDef<Bundle>[] = [
    {
      field: 'title',
      tooltipField: 'title',
      headerName: 'Bundle Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
    },
    {
      maxWidth: 80,
      cellRenderer: StripePurchaseBundleUiActionsComponent,
      cellRendererParams: {
        addCallback: (item: Bundle) => {
          this.addToCart(item, 1);
        },
      },
    },
  ];

  public readonly cartTableDef: GridOptions<PurchaseBundleEntry> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly cartColumnDefs: ColDef<PurchaseBundleEntry>[] = [
    {
      field: 'title',
      tooltipField: 'title',
      headerName: 'Bundle Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Quantity',
      maxWidth: 200,
      cellRenderer: StripePurchaseBundleUiCartActionsComponent,
      cellRendererParams: {
        plusCallback: (item: PurchaseBundleEntry) => {
          this.addToCart(item, 1);
        },
        minusCallback: (item: PurchaseBundleEntry) => {
          this.addToCart(item, -1);
        },
      },
    },
  ];

  ngOnInit(): void {
    this.isLoading.set(true);
    this.subscriptionService.getBundles(this.branch().customerType).subscribe({
      next: (bundles) => {
        this.bundles.set(bundles);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  addToCart(product: any, quantity: number) {
    // Check if the product is already in the cart
    const cartItem = this.cart().find((c) => c.id === product.id);
    if (cartItem) {
      this.cart.update((cart) => {
        const item = cart.find((c) => c.id === product.id);
        if (item) {
          item.quantity += quantity;
          if (item.quantity === 0) {
            return cart.filter((c) => c.id !== product.id);
          }
        }
        return [...cart];
      });
    } else {
      this.cart.set([...this.cart(), { ...product, quantity }]);
    }
  }

  isFormValid() {
    return this.transactionDetails.controls.internal_po.value && this.cart().length > 0;
  }

  checkout() {
    this.saving.set(true);
    this.subscriptionService.purchaseBundles(this.branch().id, this.cart(), this.transactionDetails.value).subscribe({
      next: () => {
        this.notifyService.notify('Purchase Successful');
        this.cart.set([]);
        this.saving.set(false);
      },
      error: (error) => {
        this.saving.set(false);
        if (error.error?.error) {
          this.notifyService.notify(error.error.error);
        } else {
          this.notifyService.notify('Purchase failed');
        }
      },
    });
  }
}
